/* eslint-disable no-unsafe-optional-chaining */
import './style.scss';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { getProjectById } from 'apis/project.api';
import { useCallback, useEffect, useMemo } from 'react';
import { formatCurrency, formatFileName } from 'utils/func';
import { ORGANIZATION_TYPES, TIMEZONE_VN } from 'constant';
import { downloadFile } from 'apis/projectProposals.api';
import ResearchFields from './ResearchFields';

export default function GeneralInfo({ onChangeAppliedYear }) {
  const { t } = useTranslation();
  const { projectId: paramProjectId } = useParams();
  const [searchParams] = useSearchParams();

  const projectId = paramProjectId ?? searchParams.get('projectId');
  const { data: _data } = useQuery(['projectDetailInfo'], () => getProjectById(projectId));
  const data = useMemo(() => _data?.data ?? [], [_data]);
  const { mutate: mutateDownloadFile } = useMutation(downloadFile);

  const projectProposalOrganizations = data?.projectProposal?.projectProposalOrganizations?.find(
    ({ type }) => type === ORGANIZATION_TYPES.LEAD
  );
  const projectOwner = data?.projectProposal?.projectProposalContacts?.find(
    ({ projectRole }) => projectRole.code === 'OWNER'
  );

  const participants = useMemo(
    () => [
      ...(data?.projectProposal?.projectProposalContacts ?? []).map(
        ({ fullname, organization, assignedTask }) => ({
          fullname,
          organizationName: organization.name,
          partnerContribution: assignedTask,
        })
      ),
    ],
    [data?.projectProposal]
  );

  const partners = useMemo(
    () => [
      ...(data?.projectProposal?.projectProposalOrganizations ?? []).filter(
        ({ type }) => type === ORGANIZATION_TYPES.PARTNER
      ),
    ],
    [data?.projectProposal]
  );

  const handleDownloadFile = useCallback(
    (path) => {
      mutateDownloadFile(
        {
          id: data?.projectProposal?.id,
          path,
        },
        {
          onSuccess: async (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);

              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = formatFileName(path);

              link.click();
            }
          },
        }
      );
    },
    [mutateDownloadFile, data?.projectProposal?.id]
  );

  const removeHtmlTag = (str) => str?.replace(/<\/?[^>]+(>|$)/g, '');

  useEffect(() => {
    if (onChangeAppliedYear && data) {
      onChangeAppliedYear(data?.projectProposal?.yearProposed ?? 2019); // Default year is 2019
    }
  }, [data]);

  return (
    <div className="general-container">
      {/* A */}
      <h4 className="font-bold">{t('r05.a.title')} </h4>
      {/* A1 */}
      <section>
        <h4 className="my-2">{t('r05.a.a1.title')}</h4>
        <p className="my-2">
          &emsp;- &ensp;
          <span className="font-bold inline-block">{t('r05.a.a1.vietnameseName')}&nbsp;</span>
          {data?.projectProposal?.vietnameseName}
        </p>
        <p className="my-2">
          &emsp;- &ensp;
          <span className="font-bold inline-block">{t('r05.a.a1.englishName')}&nbsp;</span>
          {data?.projectProposal?.englishName}
        </p>
      </section>
      {/* A2 */}
      <section>
        <h4>{t('r05.a.a2.title')}</h4>
        <ResearchFields
          researchFields={data?.projectProposal?.projectProposalResearchFields ?? []}
        />
      </section>
      {/* A3  */}
      <section>
        <h4>{t('r05.a.a3.title')}</h4>
        <p className="my-2">
          &emsp; <span className="font-bold">{t('r05.a.a3.name')}&nbsp;</span>
          {data?.projectProposal?.researchType?.name}
        </p>
      </section>
      {/* A4  */}
      <section>
        <h4>{t('r05.a.a4.title')}</h4>
        <p className="my-2">
          &emsp;- &ensp;{t('r05.a.a4.timeFrom')} {new Date(data?.startDate).getMonth() + 1}/
          {new Date(data?.startDate).getFullYear()} {t('r05.a.a4.timeTo')}{' '}
          {new Date(data?.endDate).getMonth() + 1}/{new Date(data?.endDate).getFullYear()}
        </p>
        <p className="my-2">
          &emsp;- &ensp;{t('r05.a.a4.renewFrom')} ............ {t('r05.a.a4.renewTo')} ...........
        </p>
      </section>
      {/* A5  */}
      <section>
        <h4>{t('r05.a.a5.title')}</h4>
        <p className="my-2">
          &emsp;- &ensp;{t('r05.a.a5.totalExpenditure')}{' '}
          <span className="font-italic">{formatCurrency(data?.totalExpenditure)}</span>{' '}
          {t('r05.a.a5.include')}
        </p>
        <p className="my-2">
          &emsp;&emsp;- &ensp;{t('r05.a.a5.expenditureFromVNU')}{' '}
          <span className="font-italic">{formatCurrency(data?.totalExpenditure)}</span>
        </p>
        <p className="my-2">
          &emsp;&emsp;&emsp; + {t('r05.a.a5.expenditure1st')} ......... {t('r05.a.a5.followUp')}{' '}
          .....
          {t('r05.a.a5.day')}
        </p>
        <p className="my-2">
          &emsp;&emsp;&emsp; + {t('r05.a.a5.expenditure2nd')} ......... {t('r05.a.a5.followUp')}{' '}
          .....
          {t('r05.a.a5.day')}
        </p>
        <p className="my-2">
          &emsp;&emsp;- &ensp;{t('r05.a.a5.expenditureFromFunding')}{' '}
          <span className="font-italic">{formatCurrency(data?.expenditureFromFunding)}</span>
        </p>
      </section>
      {/* A6  */}
      <section>
        <h4>{t('r05.a.a6.title')}</h4>
        <p className="my-2">
          <span className="font-bold inline-block">{t('r05.a.a6.name')}</span>{' '}
          {projectOwner?.contact?.fullname}
        </p>
        <p className="my-2">
          <span className="font-bold inline-block">{t('r05.a.a6.birthDate')}</span>{' '}
          {new Date(projectOwner?.dob).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
          <span className="font-bold inline-block ml-6">{t('r05.a.a6.sex')}</span>{' '}
          {projectOwner?.gender ? t(`gender.${projectOwner?.gender}`) : '.......'}
        </p>
        <p className="my-2">
          <span className="font-bold inline-block">{t('r05.a.a6.company')}</span>{' '}
          {projectOwner?.organization?.name}
        </p>
        <p className="my-2">
          <span className="inline-block font-bold">{t('r05.a.a6.phone')}</span>{' '}
          {projectOwner?.mobile}
          <span className="font-bold ml-6">{t('r05.a.a6.email')}</span>{' '}
          {projectOwner?.contact?.email}
        </p>
      </section>
      {/* A7  */}
      <section>
        <h4>{t('r05.a.a7.title')}</h4>
        <p className="my-2">
          <span className="font-bold">{t('r05.a.a7.companyName')}</span>{' '}
          {projectProposalOrganizations?.name}
        </p>
        <p className="my-2">
          <span className="font-bold">{t('r05.a.a7.leaderName')}</span>{' '}
          {projectProposalOrganizations?.chief}
        </p>
        <p className="my-2">
          <span className="font-bold">{t('r05.a.a7.phone')}</span>
          {projectProposalOrganizations?.phone} &emsp;&emsp; {t('r05.a.a7.fax')}
          {projectProposalOrganizations?.fax}
        </p>
        <p className="my-2">
          <span className="font-bold">{t('r05.a.a7.email')}</span>{' '}
          {projectProposalOrganizations?.email}
        </p>
      </section>
      {/* A8  */}

      <section>
        <h4>{t('r05.a.a9.title')}</h4>
        {partners?.length > 0 ? (
          partners.map((org, index) => (
            <div className="flex ml-5 mb-2 flex-column" key={org?.id || index}>
              <div className="mb-2">
                <span className="font-bold">
                  {t('projectProposal.general.organization.name')} {index + 1}:{' '}
                </span>
                <span>{org?.name}</span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.partners.confirmationDocument')}:{' '}
                </span>
                {org?.partnerConfirmationUrl && (
                  <span
                    className="p-link text-primary underline"
                    onClick={() => handleDownloadFile(org?.partnerConfirmationUrl)}
                  >
                    {t('projectProposal.general.partners.attachment')}
                  </span>
                )}
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.chief')}:{' '}
                </span>
                <span>{org?.chief}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.phone')}:{' '}
                  </span>
                  <span>{org?.phone}</span>
                </div>
                <div className="w-20rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.fax')}:{' '}
                  </span>
                  <span>{org?.fax}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.address')}:{' '}
                </span>
                <span>{org?.address}</span>
              </div>
              <div
                className={index === data?.projectProposalOrganizations?.length - 2 ? '' : 'mb-3'}
              >
                <div>
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.partnerContribution')}:{' '}
                  </span>
                </div>
                <p>{org?.partnerContribution}</p>
              </div>
            </div>
          ))
        ) : (
          <span>({t('projectProposal.general.partners.noPartner')})</span>
        )}
      </section>

      <section>
        <h4>{t('r05.a.a8.title')}</h4>
        <div className="form-table">
          <table className="mb-2">
            <thead>
              <tr>
                <th className="text-center" style={{ minWidth: 50, width: '5%' }}>
                  {t('r05.a.a8.order')}
                </th>
                <th className="text-center" style={{ minWidth: 180, width: '20%' }}>
                  {t('r05.a.a8.fullName')}
                </th>
                <th className="text-center" style={{ minWidth: 160, width: '20%' }}>
                  {t('r05.a.a8.workingUnit')}
                </th>
                <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                  {t('r05.a.a8.contentWorking')}
                </th>
              </tr>
            </thead>
            <tbody>
              {participants?.length > 0 ? (
                participants.map((item, index) => (
                  <tr key={`summary-report-partners-${item.fullname}`}>
                    <td className="text-center py-3" width="5%">
                      {index + 1}
                    </td>
                    <td className="py-3" width="20%">
                      {item.fullname}
                    </td>
                    <td className="py-3" width="25%">
                      {item.organizationName}
                    </td>
                    <td className="py-3" width="40%">
                      {removeHtmlTag(item?.partnerContribution ?? '')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-2">
                    {t('r05.a.a8.noPartner')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

GeneralInfo.propTypes = {
  onChangeAppliedYear: PropTypes.func.isRequired,
};
